/* eslint-disable react-hooks/refs -- intentional render-time ref read +
 * write. Mirrors `useStableMap`'s pattern: the ref tracks the
 * previously-returned Set identity so this hook can short-circuit
 * "outer Set rebuilt with the same content" cases — a common
 * symptom of `new Set(...)` being allocated by a `useMemo` whose
 * source map flipped reference but whose filtered subset is
 * unchanged. Pure with respect to render output. */
import { useRef } from "react";

/**
 * Outer-Set stability layer for hooks that rebuild a Set from a
 * source whose reference flips faster than its content. Mirrors
 * `useStableMap` for the Set case.
 *
 * If every entry in `next` is present in the previously-returned Set
 * (and the size matches), returns the previous Set reference.
 * Otherwise returns `next` and remembers it for the next call.
 *
 * Why this is needed:
 *
 * `coworkerIds = new Set(renderUserMap where is_coworker)` — every
 * `renderUserMap` reference flip rebuilds the Set, even when no
 * coworker actually changed. The fresh Set ref then flows into
 * `useMessageSend`'s `useCallback` deps, recreating `sendMessage`,
 * which busts `TiptapEditor`'s memo via `onSend`.
 *
 * Wrapping the rebuilt Set in `useStableSet` closes that loop:
 *
 *   const raw = useMemo(() => new Set(...), [renderUserMap]);
 *   const coworkerIds = useStableSet(raw);
 *
 * Now downstream consumers see a stable Set identity unless an entry
 * actually changed.
 */
export function useStableSet<T>(next: Set<T>): Set<T> {
  const prevRef = useRef<Set<T> | null>(null);
  const prev = prevRef.current;
  if (prev && prev.size === next.size) {
    let allEqual = true;
    for (const v of next) {
      if (!prev.has(v)) {
        allEqual = false;
        break;
      }
    }
    if (allEqual) return prev;
  }
  prevRef.current = next;
  return next;
}
